import React, { useState } from "react";
import PageHeader from "@/components/admin/header/PageHeader";
import SubmitAndCancelButtons from "@/components/admin/SubmitAndCancelButtons";
import LoadingSpinner from "@/components/atomic/Loading";
import { RadioGroup, RadioGroupItem } from "@/components/atomic/RadioGroup";
import Button from "@/components/ui/Button";
import { useAttributes } from "@/hooks/useAttributes";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { Attribute, AttributeType } from "@/models/attribute";
import { AttributeService } from "@/services/attribute/attribute.service";
import { showError, showSuccess } from "@/utils/notification";

const AttributeManagementPage: React.FC = () => {
  const { sessionCurrentWorkspaceId } = useWorkspaces();
  const { attributes, isLoading, mutate } = useAttributes(sessionCurrentWorkspaceId);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [localAttributes, setLocalAttributes] = useState<Attribute[]>([]);

  // 編集モードに入る
  const handleEdit = () => {
    if (attributes) {
      setLocalAttributes([...attributes]);
    }
    setIsEditing(true);
  };

  // キャンセル
  const handleCancel = () => {
    setLocalAttributes([]);
    setIsEditing(false);
  };

  // 型変更
  const handleTypeChange = (index: number, type: AttributeType) => {
    const updated = [...localAttributes];
    updated[index] = new Attribute(updated[index].name, type);
    setLocalAttributes(updated);
  };

  // 保存
  const handleSave = async () => {
    try {
      setIsSaving(true);
      await AttributeService.bulkUpsertAttributes(sessionCurrentWorkspaceId, localAttributes);
      await mutate();
      showSuccess("属性を保存しました。");
      setIsEditing(false);
      setLocalAttributes([]);
    } catch (error) {
      console.error(error);
      showError("属性の保存に失敗しました。");
    } finally {
      setIsSaving(false);
    }
  };

  const displayAttributes = isEditing ? localAttributes : attributes || [];

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* ヘッダー */}
      <div className="flex items-center justify-between w-full">
        <PageHeader title="VoC属性管理" />
        {isEditing
          ? (
            <SubmitAndCancelButtons
              isLoading={isSaving}
              onCancel={handleCancel}
              onSubmit={handleSave}
              buttonClassName="min-w-40"
            />
          )
          : (
            <Button
              disabled={isLoading || !attributes || attributes.length === 0}
              onClick={handleEdit}
              className={cn(
                "w-40 h-10 px-4 py-2 !text-sm text-white !font-bold",
                "bg-[#293d72] border-[#293d72] hover:bg-[#293d72]/90 shadow-[0px_2px_#233462]",
              )}
            >
              編集する
            </Button>
          )}
      </div>

      {/* 説明 */}
      <div className="bg-[#f1f1f1] rounded-[5px] p-4">
        <p className="text-[14px] text-[#5f6368] leading-[1.8]">
          各項目にあてはまるデータの種類を選択してください。
        </p>
        <p className="text-[12px] text-[#5f6368] leading-[1.8]">
          文章＝文字列（例：タイトル、コメントなど）／ 数字＝数値（例：年齢、感情値など）／
          選択肢が決まっているもの＝カテゴリ値（例：性別、地域、問い合わせ種別など）
        </p>
      </div>

      {/* ヘッダーラベル */}
      {!isLoading && displayAttributes.length > 0 && (
        <div>
          <div className="flex gap-9 items-center pb-2">
            <p className="text-sm font-semibold text-black w-[300px] ml-7">属性タイトル：</p>
            <p className="text-sm font-semibold text-black">データの種類：</p>
          </div>

          {/* 属性リスト */}
          <div className="flex flex-col gap-2">
            {displayAttributes.map((attr, index) => (
              <div key={attr.name} className="flex gap-9 items-center">
                {/* 番号と属性名 */}
                <div className="flex gap-2 items-center w-[328px]">
                  <p className="text-sm font-semibold text-[#333333] w-5">{index + 1}</p>
                  <div
                    className={cn(
                      "flex-1 h-[50px] px-4 py-2 rounded-lg flex items-center bg-[#f1f1f1]",
                    )}
                  >
                    <p className="text-sm font-semibold text-[#333333]">{attr.name}</p>
                  </div>
                </div>

                {/* ラジオボタン */}
                <RadioGroup
                  value={attr.type}
                  onValueChange={(value) => handleTypeChange(index, value as AttributeType)}
                >
                  <RadioGroupItem value="textual" id={`${index}-textual`} label="文字列" disabled={!isEditing} />
                  <RadioGroupItem value="numerical" id={`${index}-numerical`} label="数値" disabled={!isEditing} />
                  <RadioGroupItem
                    value="categorical"
                    id={`${index}-categorical`}
                    label="カテゴリ値"
                    disabled={!isEditing}
                  />
                </RadioGroup>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ローディング */}
      {isLoading && (
        <div className="flex justify-center items-center h-[100px]">
          <LoadingSpinner data-testid="loader" />
        </div>
      )}

      {/* データなし */}
      {!isLoading && displayAttributes.length === 0 && (
        <div className="flex justify-center items-center h-[100px]">
          <p className="text-[#5f6368]">属性データがありません。VoCファイルをアップロードしてください。</p>
        </div>
      )}
    </div>
  );
};

export default AttributeManagementPage;
